import React, { useEffect, useRef, useState } from 'react';
import jsQR from 'jsqr';
import { Camera, ImageUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const decode = (source, w, h, canvas) => {
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(source, 0, 0, w, h);
  return jsQR(ctx.getImageData(0, 0, w, h).data, w, h)?.data || null;
};

const decodeFile = (file) => new Promise((resolve) => {
  const img = new Image();
  img.onload = () => {
    const scale = Math.min(1, 1280 / Math.max(img.width, img.height));
    resolve(decode(img, Math.round(img.width * scale), Math.round(img.height * scale), document.createElement('canvas')));
    URL.revokeObjectURL(img.src);
  };
  img.onerror = () => resolve(null);
  img.src = URL.createObjectURL(file);
});

// Query parameters carried by a scanned receipt QR (full URL or bare query string).
export const paramsFromScan = (text) => {
  const t = String(text || '').trim();
  try { return Object.fromEntries(new URL(t).searchParams); } catch { /* not a full URL */ }
  const q = t.includes('?') ? t.slice(t.indexOf('?') + 1) : (t.includes('=') ? t : '');
  return q ? Object.fromEntries(new URLSearchParams(q)) : {};
};

const CameraScanner = ({ onResult, onError }) => {
  const videoRef = useRef(null);
  useEffect(() => {
    const canvas = document.createElement('canvas');
    let stream;
    let raf;
    let stopped = false;
    const tick = () => {
      const v = videoRef.current;
      if (stopped || !v) return;
      if (v.readyState >= 2 && v.videoWidth) {
        const scale = Math.min(1, 640 / v.videoWidth);
        const text = decode(v, Math.round(v.videoWidth * scale), Math.round(v.videoHeight * scale), canvas);
        if (text) { stopped = true; onResult(text); return; }
      }
      raf = requestAnimationFrame(tick);
    };
    (async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) throw new Error('unsupported');
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false });
        if (stopped) { stream.getTracks().forEach(t => t.stop()); return; }
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
        raf = requestAnimationFrame(tick);
      } catch (e) { onError(e?.name === 'NotAllowedError' ? 'denied' : 'unavailable'); }
    })();
    return () => { stopped = true; cancelAnimationFrame(raf); stream?.getTracks().forEach(t => t.stop()); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return <video ref={videoRef} playsInline muted className="w-full aspect-square object-cover rounded-xl bg-black" data-testid="qr-scan-video" />;
};

const CAMERA_MSG = {
  denied: 'Camera access was blocked. Allow camera access in your browser settings, or take a photo of the QR code instead.',
  unavailable: "We couldn't open your camera. Take a photo of the QR code instead.",
};

// Camera QR scanner (live video, photo fallback). Reads the receipt QR only — never generates one.
export const QrScanButton = ({ onScanned, disabled, primary }) => {
  const [open, setOpen] = useState(false);
  const [camError, setCamError] = useState('');
  const [photoMsg, setPhotoMsg] = useState('');
  const fileRef = useRef(null);
  const finish = (text) => { setOpen(false); onScanned(text); };
  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const text = await decodeFile(file);
    if (text) finish(text);
    else setPhotoMsg("We couldn't find a QR code in that photo. Try again closer to the receipt.");
  };
  return (
    <>
      <Button type="button" onClick={() => { setCamError(''); setPhotoMsg(''); setOpen(true); }} disabled={disabled}
        className="w-full text-white h-12 text-base" style={{ backgroundColor: primary }} data-testid="entry-scan-qr-btn">
        <Camera className="w-5 h-5 mr-2" /> Scan QR Code
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid="qr-scan-dialog">
          <DialogHeader>
            <DialogTitle>Scan the QR code on your receipt</DialogTitle>
            <DialogDescription>Hold the QR code inside the frame.</DialogDescription>
          </DialogHeader>
          {open && !camError && <CameraScanner onResult={finish} onError={setCamError} />}
          {camError && <p className="text-sm text-[#B91C1C]" data-testid="qr-scan-camera-error">{CAMERA_MSG[camError]}</p>}
          <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} data-testid="qr-scan-photo-btn">
            <ImageUp className="w-4 h-4 mr-2" /> Take a photo instead
          </Button>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPhoto} data-testid="qr-scan-photo-input" />
          {photoMsg && <p className="text-sm text-[#B91C1C]" data-testid="qr-scan-photo-error">{photoMsg}</p>}
        </DialogContent>
      </Dialog>
    </>
  );
};
